import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { readPlayerLanguage } from "../gameSetupStorage";
import {
  getDvSourceImageUrl,
  getLandingPageTitleColor,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  saveSnakesLaddersAnswerProgress,
  setStageActive,
  normalizePlayerName,
} from "../../functions/stageReportSync";
import {
  fireThemeStageDataMissingSwal,
  validateThemeStageData,
} from "../../functions/validateThemeStageData";
import {
  cellColor,
  createBoardLayout,
  plannedRoll,
  squareDestination,
  squareKind,
  squareLabel,
  squareTokenStyle,
} from "../../functions/boardLayout";
import {
  answersMatch,
  getQuizByCategory,
  getThemeLangPack,
  questionOptions,
  resolveCorrectAnswerDisplay,
  shuffleCopy,
  sleep,
} from "./snakesLaddersUtils";
import BoardLinks from "./BoardLinks";
import { playGameAudio, playHopAudio, stopGameAudio } from "./gameAudio";
import "./snakesLadders.css";

const STAGE_NUM = 1;
const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const ALL_CORRECT_BONUS = 200;
const HOP_MS = 420;
const JUMP_MS = 900;

const ASSET = `${process.env.PUBLIC_URL || ""}/img/game`;

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const lang = readPlayerLanguage();
  const pack = useMemo(() => getThemeLangPack(themeData, lang), [themeData, lang]);
  const quizPools = useMemo(
    () => getQuizByCategory(themeData, lang),
    [themeData, lang]
  );

  const layout = useMemo(
    () => createBoardLayout(themeData || {}),
    [themeData]
  );

  const color1 = themeData?.set1_color1 || "#2bb673";
  const color2 = themeData?.set1_color2 || "#3171ad";
  const color3 = themeData?.set1_color3 || "#ffffff";
  const boardText = themeData?.board_Textcolor || "#000";
  const qBox = themeData?.questionBox_color || "#fbf99f";
  const titleColor = getLandingPageTitleColor(themeData || {});
  const buttonColor = String(themeData?.button_color || "#5b9bd5").trim();
  const buttonTextColor = String(
    themeData?.button_Textcolor || themeData?.button_text_color || "#fff"
  ).trim();
  const avatar1 = getDvSourceImageUrl(themeData?.avtar1);
  const avatar2 = getDvSourceImageUrl(themeData?.avtar2);
  const playerName = useMemo(
    () => normalizePlayerName(storedUser),
    [storedUser]
  );

  const [phase, setPhase] = useState("select");
  const [myAvatar, setMyAvatar] = useState("");
  const [pos, setPos] = useState(1);
  const [dice, setDice] = useState(6);
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [question, setQuestion] = useState(null);
  const [picked, setPicked] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  /** null while playing; set when the run ends so the side panel shows win/lose. */
  const [endResult, setEndResult] = useState(null);
  /** "hop" for dice steps, "slide" for snake/ladder travel, "" when still. */
  const [tokenMotion, setTokenMotion] = useState("");
  const [hopTick, setHopTick] = useState(0);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );
  const [dbReady, setDbReady] = useState(false);
  const [timerOn, setTimerOn] = useState(false);

  const posRef = useRef(1);
  const snakeQ = useRef([]);
  const ladderQ = useRef([]);
  const snakeIdx = useRef(0);
  const ladderIdx = useRef(0);
  const eqCounterRef = useRef(0);
  const allCorrect = useRef(true);
  const moving = useRef(false);
  const finished = useRef(false);
  const answersRef = useRef([]);
  const scoreRef = useRef(0);
  const elapsedRef = useRef(0);

  const wrongPopupText =
    String(themeData?.end_game_text_popup || "").trim() ||
    pack.Wrong_Answer ||
    "Incorrect Answer!";

  const strings = useMemo(
    () => ({
      welcome: pack.welcome || "WELCOME",
      instructions: pack.instructions || "Select your avatar",
      letsRoll: pack["lets-roll"] || "Lets Roll",
      rollDice: pack["roll-dice"] || "Roll-Dice",
      score: pack.score || "Score",
      timer: pack.timer1 || "Time",
      rulebtn: pack.rulebtn || "Rules",
      close: pack.close || "Close",
      submit: pack.submit || "Submit",
      correct: pack.Correct_Answer_Text || "Correct Answer!",
      wrong: pack.Wrong_Answer || "Incorrect Answer!",
      rightIs: pack.right_Answer || "The correct answer is:",
      extra:
        pack.extra_score ||
        "You got extra 200 points because you answered all answers right!",
      ok: pack.button || "OK",
      greetings: pack.greetings || "Congratulations!",
      won: pack.won || "You Won!",
      lose: pack.loose || "You Lose!",
      greetings3: pack.greetings3 || pack.greetings2 || "Better Luck Next Time!",
      defaultText: pack["default-text"] || "SELECT YOUR ANSWER",
      rulesTitle: pack.rules_title || "Rules",
      yourTurn: "Your turn",
    }),
    [pack]
  );

  const rulesList = Array.isArray(pack.rules)
    ? pack.rules.map((r) => String(r || "").trim()).filter(Boolean)
    : Array.isArray(themeData?.rules)
      ? themeData.rules.map((r) => String(r || "").trim()).filter(Boolean)
      : [];

  const myAvatarSrc = myAvatar === "two" ? avatar2 : avatar1;
  const pointsPer = Math.max(1, parseInt(themeData?.points, 10) || 1);

  /**
   * SweetAlert2 renders into <body>, outside this component's tree, so the theme
   * colours have to live on :root for the .sl-swal rules to reach them.
   */
  useEffect(() => {
    const root = document.documentElement;
    const vars = {
      "--sl-qbox": qBox,
      "--sl-title": titleColor,
      "--sl-btn": buttonColor,
      "--sl-btn-text": buttonTextColor,
    };
    Object.entries(vars).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });
    return () => {
      Object.keys(vars).forEach((name) => root.style.removeProperty(name));
    };
  }, [qBox, titleColor, buttonColor, buttonTextColor]);

  const swalTheme = useMemo(
    () => ({
      buttonsStyling: false,
      customClass: { popup: "sl-swal", confirmButton: "sl-swal-btn" },
    }),
    []
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  // Unlock document scroll for the stage. Desktop keeps a fixed full-bleed
  // background; portrait phones use scroll attachment so iOS can pan.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("sl-stage-open");
    html.classList.add("sl-stage-open");

    const isMobilePortrait = () =>
      window.matchMedia("(max-width: 900px) and (orientation: portrait)").matches;

    const apply = () => {
      const mobile = isMobilePortrait();
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");

      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      body.style.setProperty(
        "min-height",
        mobile ? "auto" : "100dvh",
        "important"
      );
      body.style.setProperty(
        "background-attachment",
        mobile ? "scroll" : "fixed",
        "important"
      );
      body.style.setProperty("background-size", "cover", "important");
      body.style.setProperty("background-repeat", "no-repeat", "important");
      body.style.setProperty("background-position", "center center", "important");

      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
        root.style.setProperty(
          "min-height",
          mobile ? "auto" : "100dvh",
          "important"
        );
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("sl-stage-open");
      html.classList.remove("sl-stage-open");
      [
        "overflow",
        "overflow-x",
        "overflow-y",
        "height",
        "min-height",
        "max-height",
        "position",
        "touch-action",
        "background-attachment",
        "background-size",
        "background-repeat",
        "background-position",
      ].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);

  // Fill missing name / emp id in the session copy so the HUD and DB stay in sync.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return;
      const data = JSON.parse(raw) || {};
      const resolved = normalizePlayerName(data);
      const emp =
        data.employeeId || data.emp_id || data.empId || "";
      let changed = false;
      if (resolved && data.name !== resolved) {
        data.name = resolved;
        changed = true;
      }
      if (emp && !data.employeeId) {
        data.employeeId = emp;
        data.emp_id = emp;
        changed = true;
      }
      if (changed) sessionStorage.setItem("userData", JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }, [storedUser]);

  useEffect(() => {
    if (phase !== "select") return;
    snakeQ.current = shuffleCopy(quizPools.snakes);
    ladderQ.current = shuffleCopy(quizPools.ladders);
    snakeIdx.current = 0;
    ladderIdx.current = 0;
  }, [quizPools, phase]);

  useEffect(() => {
    if (!themeData || themeStatus === "loading") return undefined;
    const validation = validateThemeStageData(themeData, {});
    if (!validation.ok) {
      fireThemeStageDataMissingSwal(validation.missing);
    }
    return undefined;
  }, [themeData, themeStatus]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setStageActive(STAGE_NUM, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        const st = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {});
          if (redirect && redirect !== "/stage1" && redirect !== "/game") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error("Game bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, storedUser]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const moveTo = (square) => {
    posRef.current = square;
    setPos(square);
  };

  /** Walk square by square with a hop — not a continuous slide. */
  const hopTo = async (from, to) => {
    if (to === from) return to;
    const dir = to > from ? 1 : -1;
    let cur = from;
    setTokenMotion("hop");
    while (cur !== to) {
      cur += dir;
      // Flip the tick so the bounce keyframe restarts on every square.
      setHopTick((tick) => tick + 1);
      playHopAudio(HOP_MS);
      moveTo(cur);
      await sleep(HOP_MS);
    }
    stopGameAudio("move");
    setTokenMotion("");
    return to;
  };

  /** Snakes and ladders slide the token in one continuous move. */
  const jumpTo = async (to, kind) => {
    stopGameAudio("move");
    if (kind === "snake") playGameAudio("snake");
    else if (kind === "ladder") playGameAudio("ladder");
    setTokenMotion("slide");
    moveTo(to);
    await sleep(JUMP_MS);
    setTokenMotion("");
    return to;
  };

  const takeQuestion = (kind) => {
    const pool = kind === "snake" ? snakeQ : ladderQ;
    const idx = kind === "snake" ? snakeIdx : ladderIdx;
    if (idx.current >= pool.current.length) return null;
    const q = pool.current[idx.current];
    idx.current += 1;
    return q;
  };

  const isPoolExhausted = (kind) =>
    kind === "snake"
      ? snakeIdx.current >= snakeQ.current.length
      : ladderIdx.current >= ladderQ.current.length;

  const persistAndLeave = useCallback(
    async (finalScore, playerWon) => {
      const finalTime = formatClock(elapsedRef.current);
      setTimerOn(false);
      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
            stageNumber: STAGE_NUM,
            score: finalScore,
            total_score: finalScore,
            time: finalTime,
            gameover: true,
            user_answers: answersRef.current,
            winner: playerWon ? "win" : "loose",
          });
          if (saveResult?.stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
            setStageRowId(String(saveResult.stageId));
          }
        }
      } catch (err) {
        console.error("Game finish:", err);
      }
      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      if (isDemoBypass) {
        window.location.href = DEMO_PLANS_URL;
        return;
      }
      navigate("/thankyou", {
        replace: true,
        state: { points: finalScore, time: finalTime, won: playerWon },
      });
    },
    [adminToken, backendBase, isDemoBypass, navigate, stageRowId, storedUser]
  );

  const finishGame = useCallback(
    async (playerWon) => {
      if (finished.current) return;
      finished.current = true;
      moving.current = false;
      setBusy(false);
      setQuestion(null);
      setTimerOn(false);

      let finalScore = scoreRef.current;
      if (playerWon && allCorrect.current) {
        finalScore += ALL_CORRECT_BONUS;
        scoreRef.current = finalScore;
        setScore(finalScore);
        // Bonus first as its own popup; win/lose panel follows after OK.
        await Swal.fire({
          ...swalTheme,
          title: strings.extra,
          confirmButtonText: strings.ok,
        });
      }

      setEndResult({
        won: playerWon,
        finalScore,
      });
    },
    [strings, swalTheme]
  );

  // Win/lose panel: no OK button — leave automatically after 5 seconds.
  useEffect(() => {
    if (!endResult) return undefined;
    const timer = window.setTimeout(() => {
      persistAndLeave(endResult.finalScore, endResult.won);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [endResult, persistAndLeave]);

  const showCorrectPopup = useCallback(
    async (information) => {
      playGameAudio("right");
      const detail = String(information || "").trim();
      await Swal.fire({
        ...swalTheme,
        icon: undefined,
        imageUrl: undefined,
        title: strings.correct,
        ...(detail ? { text: detail } : {}),
        confirmButtonText: strings.ok,
      });
    },
    [strings, swalTheme]
  );

  const showWrongPopup = useCallback(
    async (correctAnswer, information) => {
      playGameAudio("wrong");
      const detail = `${strings.rightIs} ${correctAnswer || ""}${
        information ? `\n\n${information}` : ""
      }`.trim();
      await Swal.fire({
        ...swalTheme,
        icon: undefined,
        imageUrl: undefined,
        title: strings.wrong,
        text: detail || wrongPopupText,
        confirmButtonText: strings.ok,
      });
    },
    [strings, swalTheme, wrongPopupText]
  );

  const saveProgressAfterAnswer = useCallback(
    async (pointsThisAnswer) => {
      if (isDemoBypass || !backendBase || !adminToken) return;
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId) return;
      try {
        await saveSnakesLaddersAnswerProgress({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          answerIndex: Math.min(eqCounterRef.current, 13),
          pointsThisAnswer,
          totalScore: scoreRef.current,
          time: formatClock(elapsedRef.current),
          userAnswers: answersRef.current,
        });
      } catch (err) {
        console.error("saveSnakesLaddersAnswerProgress:", err);
      }
    },
    [adminToken, backendBase, isDemoBypass, stageRowId, storedUser]
  );

  const rollDice = async () => {
    if (moving.current || finished.current || question) return;
    if (phase !== "play") return;
    moving.current = true;
    setBusy(true);

    const from = posRef.current;
    const roll = plannedRoll(layout, from);
    playGameAudio("dice");
    for (let i = 0; i < 10; i += 1) {
      setDice(1 + Math.floor(Math.random() * 6));
      await sleep(70);
    }
    setDice(roll);
    await sleep(250);

    const landed = await hopTo(from, Math.min(layout.size, from + roll));
    if (landed >= layout.size) {
      await finishGame(true);
      return;
    }

    const kind = squareKind(layout, landed);
    const q = kind ? takeQuestion(kind) : null;
    if (q) {
      setPicked("");
      setQuestion({
        ...q,
        kind,
        square: landed,
        dest: squareDestination(layout, landed),
      });
      return;
    }

    // No question left for this square type — apply the snake/ladder outcome anyway.
    if (kind) await jumpTo(squareDestination(layout, landed), kind);
    moving.current = false;
    setBusy(false);
  };

  const submitAnswer = async () => {
    if (!question || finished.current) return;
    if (!picked) {
      Swal.fire({
        ...swalTheme,
        title: strings.defaultText,
        confirmButtonText: strings.ok,
      });
      return;
    }

    const correct = answersMatch(picked, question);
    eqCounterRef.current += 1;
    answersRef.current.push({
      questionId: question.id,
      questionText: question.question_name,
      userAnswer: picked,
      correctAnswer: question.correct_answer,
      isCorrect: correct,
      timestamp: new Date().toISOString(),
      position: question.square,
      type: question.kind,
      questionNumber: eqCounterRef.current,
      answeredAt: new Date().toISOString(),
    });

    const { kind, dest, information } = question;
    let pointsThisAnswer = 0;

    setQuestion(null);
    setPicked("");

    if (correct) {
      pointsThisAnswer = pointsPer;
      scoreRef.current += pointsPer;
      setScore(scoreRef.current);
      await showCorrectPopup(information);
      // Correct on a ladder climbs it; correct on a snake means you dodge it.
      if (kind === "ladder") await jumpTo(dest, "ladder");
    } else {
      allCorrect.current = false;
      await showWrongPopup(resolveCorrectAnswerDisplay(question), information);
      // Wrong on a snake slides you down; wrong on a ladder means you stay put.
      if (kind === "snake") await jumpTo(dest, "snake");
    }

    await saveProgressAfterAnswer(pointsThisAnswer);

    if (posRef.current >= layout.size) {
      await finishGame(true);
      return;
    }

    // Legacy rule: running out of questions for a square type ends the run as a loss.
    if (isPoolExhausted(kind)) {
      await finishGame(false);
      return;
    }

    moving.current = false;
    setBusy(false);
  };

  const startGame = () => {
    if (!myAvatar) {
      Swal.fire({
        ...swalTheme,
        title: strings.instructions,
        confirmButtonText: strings.ok,
      });
      return;
    }
    snakeQ.current = shuffleCopy(quizPools.snakes);
    ladderQ.current = shuffleCopy(quizPools.ladders);
    snakeIdx.current = 0;
    ladderIdx.current = 0;
    posRef.current = 1;
    setPos(1);
    setDice(6);
    setScore(0);
    scoreRef.current = 0;
    elapsedRef.current = 0;
    setElapsedSec(0);
    answersRef.current = [];
    eqCounterRef.current = 0;
    allCorrect.current = true;
    finished.current = false;
    moving.current = false;
    setBusy(false);
    setQuestion(null);
    setPicked("");
    setEndResult(null);
    setTimerOn(true);
    setPhase("play");

    if (!snakeQ.current.length && !ladderQ.current.length) {
      Swal.fire({
        ...swalTheme,
        title: "No questions found",
        text: "This theme has no Snake or Ladder questions configured, so the board will play without them.",
        confirmButtonText: strings.ok,
      });
    }
  };

  const opts = question ? questionOptions(question) : [];

  if ((themeStatus === "loading" && !themeData) || !dbReady) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className="sl-game"
      style={{ "--sl-cols": layout.cols, "--sl-rows": layout.rows }}
    >
      <div className="sl-scoreboard">
        <div className="sl-hud-left">
          <h4 className="sl-pill">
            {strings.timer}: {formatClock(elapsedSec)}
          </h4>
        </div>
        <div className="sl-hud-right">
          <button
            type="button"
            className="sl-pill sl-rules-open"
            onClick={() => setRulesOpen(true)}
          >
            {strings.rulebtn}
          </button>
          <h4 className="sl-pill">
            {strings.score}: {score}
          </h4>
        </div>
      </div>

      <div className="sl-mainboard">
        <div className="sl-board-frame">
          <div
            className="sl-board"
            style={{
              gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
              gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
              aspectRatio: `${layout.cols} / ${layout.rows}`,
            }}
          >
            {layout.visualSquares.map((n) => (
              <div
                key={n}
                className={
                  layout.ornaments[n] ? "sl-cell sl-cell--marker" : "sl-cell"
                }
                style={{
                  backgroundColor: cellColor(layout, n, color1, color2, color3),
                  color: boardText,
                }}
              >
                {layout.ornaments[n] ? (
                  <img
                    className="sl-cell-icon"
                    src={`${ASSET}/${layout.ornaments[n]}.png`}
                    alt=""
                  />
                ) : null}
                <span>{squareLabel(layout, n)}</span>
              </div>
            ))}

            <BoardLinks layout={layout} />

            {phase === "play" && myAvatarSrc ? (
              <img
                src={myAvatarSrc}
                alt=""
                className={
                  tokenMotion === "hop"
                    ? "sl-token is-hopping"
                    : tokenMotion === "slide"
                      ? "sl-token is-sliding"
                      : "sl-token"
                }
                style={{
                  ...squareTokenStyle(layout, pos),
                  // Alternating names restart the bounce on each hop.
                  ...(tokenMotion === "hop"
                    ? {
                        animationName:
                          hopTick % 2 === 0 ? "sl-token-hop-a" : "sl-token-hop-b",
                      }
                    : null),
                }}
              />
            ) : null}
          </div>
        </div>

        <aside
          className={
            phase === "select" || question || endResult
              ? "sl-side sl-side--overlay"
              : "sl-side"
          }
        >
          <div className="sl-side-base">
            <div className="sl-info-main">
              <p className="sl-turn">{playerName || strings.yourTurn}</p>
              {myAvatarSrc ? (
                <img className="sl-player-avatar" src={myAvatarSrc} alt="" />
              ) : null}
            </div>
            <button
              type="button"
              className="sl-dice-roll"
              disabled={busy || phase !== "play" || Boolean(question) || Boolean(endResult)}
              onClick={rollDice}
            >
              <img
                className="sl-dice-img"
                src={`${ASSET}/dice${dice}.png`}
                alt=""
              />
              <span className="sl-roll-label">{strings.rollDice}</span>
            </button>
          </div>

          {phase === "select" ? (
            <div className="sl-panel">
              <h2 className="sl-welcome">{strings.welcome}</h2>
              <p className="sl-instructions">{strings.instructions}</p>
              <div className="sl-avatar-pick">
                <label className={myAvatar === "one" ? "is-on" : ""}>
                  <input
                    type="radio"
                    name="sl-avatar"
                    checked={myAvatar === "one"}
                    onChange={() => setMyAvatar("one")}
                  />
                  {avatar1 ? <img src={avatar1} alt="Avatar 1" /> : <span>1</span>}
                </label>
                <label className={myAvatar === "two" ? "is-on" : ""}>
                  <input
                    type="radio"
                    name="sl-avatar"
                    checked={myAvatar === "two"}
                    onChange={() => setMyAvatar("two")}
                  />
                  {avatar2 ? <img src={avatar2} alt="Avatar 2" /> : <span>2</span>}
                </label>
              </div>
              <div className="sl-q-submit-wrap">
                <button
                  type="button"
                  className="sl-game-btn"
                  disabled={!myAvatar}
                  onClick={startGame}
                >
                  {strings.letsRoll}
                </button>
              </div>
            </div>
          ) : null}

          {question && !endResult ? (
            <div className="sl-panel">
              <h3 className="sl-q-title">{question.question_name}</h3>
              <p className="sl-q-hint">{strings.defaultText}</p>
              <div className="sl-q-opts">
                {opts.map((opt, i) => (
                  <label
                    key={`${opt}-${i}`}
                    className={picked === opt ? "is-on" : ""}
                  >
                    <input
                      type="radio"
                      name="sl-q"
                      checked={picked === opt}
                      onChange={() => setPicked(opt)}
                    />
                    <b>{OPTION_LETTERS[i]}</b>
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
              <div className="sl-q-submit-wrap">
                <button
                  type="button"
                  className="sl-game-btn"
                  onClick={submitAnswer}
                >
                  {strings.submit}
                </button>
              </div>
            </div>
          ) : null}

          {endResult ? (
            <div className="sl-panel sl-panel--result">
              <h2 className="sl-welcome">
                {endResult.won ? strings.greetings : strings.greetings3}
              </h2>
              {myAvatarSrc ? (
                <img
                  className="sl-result-panel-avatar"
                  src={myAvatarSrc}
                  alt=""
                />
              ) : null}
              <p className="sl-result-panel-line">
                {endResult.won ? strings.won : strings.lose}
              </p>
            </div>
          ) : null}
        </aside>
      </div>

      {rulesOpen ? (
        <div className="sl-modal-overlay" role="dialog" aria-modal="true">
          <div className="sl-modal-card">
            <h2 className="sl-welcome">{strings.rulesTitle}</h2>
            <ul className="sl-inline-rules">
              {rulesList.length ? (
                rulesList.map((rule, i) => (
                  <li key={`${i}-${rule.slice(0, 12)}`}>{rule}</li>
                ))
              ) : (
                <li>No rules defined</li>
              )}
            </ul>
            <button
              type="button"
              className="sl-game-btn"
              onClick={() => setRulesOpen(false)}
            >
              {strings.close}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sl-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
